import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Compass, Moon, Mountain, Heart,
  Users, Camera, UtensilsCrossed, Sparkles, Calendar, Tag,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFormatPrice } from "@/contexts/CurrencyContext";
import { PACKS, PACK_FAMILIES } from "@/lib/packsData";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageHero from "@/components/PageHero";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Map family → icon (used in the editorial chip beneath each card title)
const FAMILY_ICON = {
  desert: Moon,
  mountain: Mountain,
  heritage: Compass,
  romance: Heart,
  family: Users,
  creative: Camera,
  table: UtensilsCrossed,
};

// EUR formatter — kept as a backup if the global currency context
// somehow isn't ready (e.g. SSR). UI uses `useFormatPrice().fromEUR`.
const formatPrice = (eur) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(eur);

// ──────────────────────────────────────────────────────────────────────────
// PackCard — one immersive editorial card. Alternates direction by index.
const PackCard = ({ pack, index, lang }) => {
  const { fromEUR } = useFormatPrice();
  const Icon = FAMILY_ICON[pack.family] || Sparkles;
  const reversed = index % 2 === 1;
  const fromLabel = pick({ en: "From", fr: "À partir de", es: "Desde" }, lang);
  const includedLabel = pick({ en: "What's woven in", fr: "Ce qui est tissé", es: "Lo que se teje" }, lang);
  const paletteLabel = pick({ en: "Palette", fr: "Palette", es: "Paleta" }, lang);
  const cta = pick({ en: "Compose this pack", fr: "Composer ce pack", es: "Componer este pack" }, lang);
  const detailCta = pick({ en: "View the pack", fr: "Découvrir le pack", es: "Ver el pack" }, lang);
  const nightsLabel = pick({ en: "nights", fr: "nuits", es: "noches" }, lang);

  return (
    <article
      className="pack-card group grid lg:grid-cols-12 gap-8 lg:gap-14 items-center"
      data-testid={`pack-card-${pack.slug}`}
      data-cms-scope={`pack-card.${pack.slug}`}
      style={{ "--accent": pack.accent }}
    >
      {/* Image */}
      <div className={`lg:col-span-7 ${reversed ? "lg:order-2" : ""}`}>
        <Link
          to={`/booking?pack=${pack.slug}`}
          className="block relative aspect-[5/4] md:aspect-[16/11] overflow-hidden bg-[#1A1513]"
          data-testid={`pack-image-${pack.slug}`}
        >
          <img
            src={pack.image}
            alt={pick(pack.title, lang)}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105 ken-burns"
            data-cms-role="card.image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/65 via-[#1A1513]/10 to-transparent" />
          <div className="film-grain" />
          {/* Overlay meta */}
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 text-[#FDFBF7]">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase">
              <Icon className="w-3.5 h-3.5" strokeWidth={1.6} style={{ color: pack.accent }} />
              <span style={{ color: pack.accent }}>{pick(pack.overline, lang)}</span>
            </div>
            <div className="mt-2 flex items-baseline gap-3 font-serif-x">
              <span className="text-3xl md:text-4xl tracking-tight">
                {pack.nights}
              </span>
              <span className="text-[11px] tracking-[0.3em] uppercase opacity-80">
                {nightsLabel}
              </span>
            </div>
          </div>
          {/* Corner ribbon — index */}
          <div
            className="absolute top-6 left-6 w-12 h-12 border border-[#FDFBF7]/40 flex items-center justify-center font-serif-x text-[#FDFBF7] text-lg"
            data-testid={`pack-ribbon-${pack.slug}`}
          >
            {String(index + 1).padStart(2, "0")}
          </div>
        </Link>
      </div>

      {/* Copy */}
      <div className={`lg:col-span-5 ${reversed ? "lg:order-1" : ""}`}>
        <p className="overline" style={{ color: pack.accent }}>
          {pick(pack.overline, lang)}
        </p>
        <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tight text-[#2C2621]">
          {pick(pack.title, lang)}
        </h2>
        <p className="mt-6 text-lg leading-relaxed text-[#3C342D]">
          {pick(pack.tagline, lang)}
        </p>

        {/* Includes */}
        <div className="mt-9 border-t border-[#2C2621]/12 pt-7">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248]">
            {includedLabel}
          </p>
          <ul className="mt-4 space-y-3" data-testid={`pack-includes-${pack.slug}`}>
            {pack.includes.map((it) => (
              <li
                key={`${pack.slug}-inc-${it.en}`}
                className="flex items-start gap-3 text-[15px] text-[#2C2621] leading-snug"
              >
                <span
                  className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: pack.accent }}
                  aria-hidden="true"
                />
                {pick(it, lang)}
              </li>
            ))}
          </ul>
        </div>

        {/* Palette + price */}
        <div className="mt-8 grid grid-cols-2 gap-px bg-[#2C2621]/10 border border-[#2C2621]/10">
          <div className="bg-[#FDFBF7] px-5 py-4">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248]">{paletteLabel}</p>
            <p className="mt-1.5 font-serif-x italic text-base text-[#2C2621]">
              {pick(pack.palette, lang)}
            </p>
          </div>
          <div className="bg-[#FDFBF7] px-5 py-4">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248]">{fromLabel}</p>
            <p
              className="mt-1.5 font-serif-x text-2xl leading-none tabular-nums"
              style={{ color: pack.accent }}
              data-testid={`pack-price-${pack.slug}`}
            >
              {fromEUR(pack.from_price_eur) || formatPrice(pack.from_price_eur)}
            </p>
          </div>
        </div>

        {/* CTAs */}
        <div className="mt-9 flex flex-wrap items-center gap-5">
          <Link
            to={`/booking?pack=${pack.slug}`}
            className="inline-flex items-center gap-3 px-7 py-4 text-[11px] tracking-[0.25em] uppercase text-[#FDFBF7] transition-all duration-300 hover:translate-y-[-1px]"
            style={{ backgroundColor: pack.accent }}
            data-testid={`pack-cta-book-${pack.slug}`}
          >
            {cta}
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
          </Link>
          <Link
            to={`/contact?topic=pack-${pack.slug}`}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase border-b border-[#2C2621]/40 pb-1 text-[#2C2621] hover:border-[#C16542] hover:text-[#C16542] transition-colors"
            data-testid={`pack-cta-detail-${pack.slug}`}
          >
            {detailCta}
            <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </article>
  );
};

// ──────────────────────────────────────────────────────────────────────────
const PacksPage = () => {
  const { lang } = useLanguage();
  const [family, setFamily] = useState("all");

  const filtered = useMemo(
    () => (family === "all" ? PACKS : PACKS.filter((p) => p.family === family)),
    [family]
  );

  const heroOverline = pick(
    { en: "Curated packs · Composed journeys", fr: "Packs sur mesure · Voyages composés", es: "Packs curados · Viajes compuestos" },
    lang
  );
  const heroTitle = pick(
    { en: "Eight ways to make the south yours.", fr: "Huit façons de faire vôtre le sud.", es: "Ocho maneras de hacer suyo el sur." },
    lang
  );
  const heroSub = pick(
    {
      en: "Hand-stitched itineraries — dunes, kasbahs, ridge lines and tables — composed by our concierge to fit the texture of the trip you're dreaming. Pick one, message us, and we'll make it yours.",
      fr: "Itinéraires cousus à la main — dunes, kasbahs, lignes de crête et tables — composés par notre conciergerie pour épouser la texture du voyage dont vous rêvez. Choisissez-en un, écrivez-nous, et nous le rendrons vôtre.",
      es: "Itinerarios cosidos a mano — dunas, kasbahs, cordales y mesas — compuestos por nuestra conserjería para encajar con la textura del viaje que sueña. Elija uno, escríbanos y lo haremos suyo.",
    },
    lang
  );
  const filterLabel = pick({ en: "Filter by mood", fr: "Filtrer par envie", es: "Filtrar por mood" }, lang);
  const countLabel = pick(
    { en: `${filtered.length} pack${filtered.length === 1 ? "" : "s"}`, fr: `${filtered.length} pack${filtered.length === 1 ? "" : "s"}`, es: `${filtered.length} pack${filtered.length === 1 ? "" : "s"}` },
    lang
  );

  return (
    <div className="bg-[#FDFBF7]" data-testid="page-packs">
      {/* HERO — multi-slide (video first, then pack photography) */}
      <section data-testid="packs-hero">
        <PageHero
          images={[
            "https://images.unsplash.com/photo-1523805009345-7448845a9e53?auto=format&fit=crop&w=2400&q=80",
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/4p3qhcbe_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-3.jpg",
            "https://images.unsplash.com/photo-1473580044384-7ba9967e16a0?auto=format&fit=crop&w=2400&q=80",
          ]}
          alt="Packs · Grup Xaluca"
          scope="page.packs"
          overline={heroOverline}
          title={heroTitle}
          sub={heroSub}
          ctaTo="#packs-list"
          ctaLabel={pick({ en: "See the eight packs", fr: "Voir les huit packs", es: "Ver los ocho packs" }, lang)}
          secondaryTo="/contact?topic=pack-bespoke"
          secondaryLabel={pick({ en: "Or compose a bespoke pack", fr: "Ou composer un pack sur mesure", es: "O componer un pack a medida" }, lang)}
        />
      </section>

      {/* FILTER BAR */}
      <section
        className="sticky top-[64px] md:top-[72px] z-30 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-[#2C2621]/10"
        data-testid="packs-filter-bar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[#5C5248]">
              <Tag className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>{filterLabel}</span>
              <span className="hidden md:inline-block text-[#C16542]">· {countLabel}</span>
            </div>
            <div className="flex flex-wrap gap-2" role="tablist">
              {PACK_FAMILIES.map((f) => (
                <button
                  key={`fam-${f.id}`}
                  type="button"
                  onClick={() => setFamily(f.id)}
                  role="tab"
                  aria-selected={family === f.id}
                  className={`text-[10px] tracking-[0.25em] uppercase px-3.5 py-2 border transition-all duration-200 ${
                    family === f.id
                      ? "bg-[#2C2621] text-[#FDFBF7] border-[#2C2621]"
                      : "border-[#2C2621]/15 text-[#5C5248] hover:border-[#C16542] hover:text-[#C16542]"
                  }`}
                  data-testid={`packs-filter-${f.id}`}
                >
                  {pick(f.label, lang)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PACKS LIST */}
      <section
        id="packs-list"
        className="py-20 md:py-28 lg:py-32"
        data-testid="packs-list"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {filtered.length === 0 ? (
            <div className="text-center py-32" data-testid="packs-empty">
              <Compass className="w-10 h-10 mx-auto text-[#A07042]" strokeWidth={1.2} />
              <p className="mt-6 font-serif-x text-3xl text-[#2C2621]">
                {pick(
                  { en: "Nothing in that mood — yet.", fr: "Rien dans ce mood — pour l'instant.", es: "Nada en ese mood — todavía." },
                  lang
                )}
              </p>
              <p className="mt-3 text-sm text-[#5C5248]">
                {pick(
                  { en: "Tell us the kind of trip you're dreaming and we'll write it.", fr: "Dites-nous le voyage dont vous rêvez et nous l'écrirons.", es: "Cuéntenos el viaje que sueña y lo escribiremos." },
                  lang
                )}
              </p>
              <Link
                to="/contact?topic=pack-bespoke"
                className="mt-8 inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-7 py-3.5 text-[11px] tracking-[0.25em] uppercase transition-colors"
                data-testid="packs-empty-cta"
              >
                {pick({ en: "Compose a bespoke pack", fr: "Composer un pack sur mesure", es: "Componer un pack a medida" }, lang)}
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </Link>
            </div>
          ) : (
            <div className="space-y-24 md:space-y-32 lg:space-y-40">
              {filtered.map((pack, i) => (
                <PackCard
                  key={`pack-${pack.slug}`}
                  pack={pack}
                  index={i}
                  lang={lang}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* BESPOKE CTA — bottom band */}
      <section
        className="bg-[#1A1513] text-[#FDFBF7] py-24 md:py-32"
        data-testid="packs-bespoke-band"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Sparkles className="w-6 h-6 mx-auto text-[#D4A373]" strokeWidth={1.5} />
          <p className="overline !text-[#D4A373] mt-5">
            {pick({ en: "None quite right?", fr: "Aucun ne vous ressemble ?", es: "¿Ninguno encaja del todo?" }, lang)}
          </p>
          <h2 className="font-serif-x mt-5 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            {pick(
              {
                en: "We write packs the way tailors cut suits — by hand, to your measure.",
                fr: "Nous écrivons les packs comme les tailleurs coupent les costumes — à la main, à vos mesures.",
                es: "Escribimos los packs como los sastres cortan trajes — a mano, a su medida.",
              },
              lang
            )}
          </h2>
          <p className="mt-7 max-w-2xl mx-auto text-base md:text-lg text-[#FDFBF7]/75 leading-relaxed">
            {pick(
              {
                en: "Tell us the houses you love, the rhythm you want, the people you're travelling with — our concierge composes a bespoke pack and a price within 24 hours.",
                fr: "Dites-nous les maisons que vous aimez, le rythme que vous voulez, les personnes avec qui vous voyagez — notre conciergerie compose un pack sur mesure et un prix sous 24 heures.",
                es: "Cuéntenos las casas que ama, el ritmo que quiere, las personas con quien viaja — nuestra conserjería compone un pack a medida y un precio en menos de 24 horas.",
              },
              lang
            )}
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-5">
            <Link
              to="/contact?topic=pack-bespoke"
              className="inline-flex items-center gap-3 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-8 py-4 text-[11px] tracking-[0.25em] uppercase transition-colors"
              data-testid="packs-bespoke-cta"
            >
              <Calendar className="w-3.5 h-3.5" strokeWidth={1.6} />
              {pick({ en: "Start a bespoke enquiry", fr: "Démarrer une demande sur mesure", es: "Iniciar una solicitud a medida" }, lang)}
            </Link>
            <Link
              to="/booking"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase border-b border-[#FDFBF7]/40 pb-1 hover:border-[#D4A373] hover:text-[#D4A373] transition-colors"
              data-testid="packs-bespoke-booking"
            >
              {pick({ en: "Or open the detailed booking form", fr: "Ou ouvrir la demande détaillée", es: "O abrir la solicitud detallada" }, lang)}
              <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
            </Link>
          </div>
          {/* Tiny features bar */}
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#FDFBF7]/10 border border-[#FDFBF7]/10 max-w-3xl mx-auto">
            {[
              { icon: Check, label: { en: "Hand-written reply · 24h", fr: "Réponse écrite à la main · 24 h", es: "Respuesta a mano · 24 h" } },
              { icon: Check, label: { en: "No booking engine · a real host", fr: "Pas de moteur de réservation · un hôte réel", es: "Sin motor de reserva · un anfitrión real" } },
              { icon: Check, label: { en: "All-season composition", fr: "Composition toutes saisons", es: "Composición todo el año" } },
            ].map((b) => {
              const I = b.icon;
              return (
                <div
                  key={`bullet-${b.label.en}`}
                  className="bg-[#1A1513] p-5 flex items-center gap-3 text-[11px] tracking-[0.2em] uppercase text-[#FDFBF7]/85"
                >
                  <I className="w-3.5 h-3.5 text-[#D4A373]" strokeWidth={1.5} />
                  {pick(b.label, lang)}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PacksPage;
